import { Helmet } from 'react-helmet-async'
import { FadeIn } from '@components/animations/FadeIn/FadeIn'
import {
  Landmark,
  Monitor,
  GraduationCap,
  Stethoscope,
  Building2,
  Cloud,
  Cpu,
  Shield,
  ArrowRight,
} from 'lucide-react'
import { Link } from 'react-router-dom'

const verticals = [
  {
    icon: Landmark,
    title: 'Government & Public Sector',
    description: 'Complete execution support for Central & State Government departments, Municipal Corporations, and Public Sector Organizations. From e-Governance platforms to digital service delivery.',
    offerings: [
      'e-Governance Platforms',
      'Citizen Service Portals',
      'Department Automation',
      'Public Distribution Systems',
    ],
    color: 'from-blue-500 to-blue-600',
  },
  {
    icon: Monitor,
    title: 'Digital Transformation',
    description: 'End-to-end digitization services helping organizations modernize operations, improve efficiency, and deliver better outcomes.',
    offerings: [
      'Enterprise Software',
      'Cloud Migration',
      'Process Automation',
      'Data Analytics',
    ],
    color: 'from-purple-500 to-purple-600',
  },
  {
    icon: GraduationCap,
    title: 'Education Technology',
    description: 'Transforming learning experiences with smart classrooms, digital content, and comprehensive education management systems.',
    offerings: [
      'Smart Classrooms',
      'Learning Management Systems',
      'Virtual Labs',
      'Skill Development Platforms',
    ],
    color: 'from-orange-500 to-orange-600',
  },
  {
    icon: Stethoscope,
    title: 'Healthcare Solutions',
    description: 'Modernizing healthcare infrastructure with hospital management systems, telemedicine, and public health platforms.',
    offerings: [
      'Hospital Management Systems',
      'Telemedicine Platforms',
      'Health Records (EMR)',
      'Public Health Dashboards',
    ],
    color: 'from-red-500 to-red-600',
  },
  {
    icon: Building2,
    title: 'Infrastructure Development',
    description: 'Building physical and digital infrastructure for educational institutions, government facilities, and corporate campuses.',
    offerings: [
      'Smart Campus Solutions',
      'Sports Facilities',
      'Laboratory Setup',
      'Campus Automation',
    ],
    color: 'from-green-500 to-green-600',
  },
  {
    icon: Cloud,
    title: 'Cloud Services',
    description: 'Comprehensive cloud solutions including migration, management, and optimization across major cloud platforms.',
    offerings: [
      'Cloud Migration',
      'Managed Services',
      'DevOps & CI/CD',
      'Cost Optimization',
    ],
    color: 'from-sky-500 to-sky-600',
  },
  {
    icon: Cpu,
    title: 'IoT & Smart Solutions',
    description: 'Connected devices and smart monitoring systems for asset tracking, environmental monitoring, and automation.',
    offerings: [
      'Smart Monitoring',
      'Asset Tracking',
      'Environmental Sensors',
      'Predictive Maintenance',
    ],
    color: 'from-teal-500 to-teal-600',
  },
  {
    icon: Shield,
    title: 'Security & Surveillance',
    description: 'Comprehensive security solutions including CCTV, access control, and integrated command centers.',
    offerings: [
      'CCTV Systems',
      'Access Control',
      'Video Analytics',
      'Command Centers',
    ],
    color: 'from-indigo-500 to-indigo-600',
  },
]

const BusinessVerticals = () => {
  return (
    <>
      <Helmet>
        <title>Business Verticals | Eonixa Dynamics</title>
        <meta name="description" content="Explore our business verticals - Government, Education, Healthcare, Infrastructure, Cloud, IoT and Security solutions" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 pt-32 pb-20">
        <div className="section-container">
          <FadeIn>
            <div className="text-center mb-16">
              <h1 className="text-4xl font-bold text-gray-900">Business Verticals</h1>
              <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
                Comprehensive solutions across multiple sectors.
                We bring domain expertise and technology excellence to every vertical.
              </p>
            </div>
          </FadeIn>

          <div className="grid gap-8 lg:grid-cols-2">
            {verticals.map((vertical, index) => {
              const Icon = vertical.icon
              return (
                <FadeIn key={vertical.title} delay={index * 0.1}>
                  <div className="group h-full rounded-2xl bg-white p-8 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-card">
                    <div className="flex items-start gap-4 mb-6">
                      <div
                        className={`flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${vertical.color} text-white shadow-lg`}
                      >
                        <Icon className="h-7 w-7" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">
                          {vertical.title}
                        </h3>
                      </div>
                    </div>

                    <p className="text-gray-600 mb-6">{vertical.description}</p>

                    <ul className="space-y-2 mb-6">
                      {vertical.offerings.map((offering) => (
                        <li
                          key={offering}
                          className="flex items-center gap-2 text-sm text-gray-600"
                        >
                          <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
                          {offering}
                        </li>
                      ))}
                    </ul>
                  </div>
                </FadeIn>
              )
            })}
          </div>

          <FadeIn delay={0.6} className="mt-16 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Ready to Explore Solutions for Your Sector?
            </h2>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Our experts are ready to understand your requirements and propose
              tailored solutions that drive impact.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-xl bg-primary-600 px-8 py-4 text-base font-semibold text-white transition-all hover:bg-primary-700"
            >
              Get in Touch
              <ArrowRight className="h-5 w-5" />
            </Link>
          </FadeIn>
        </div>
      </div>
    </>
  )
}

export default BusinessVerticals
